import React from "react";

import * as Sentry from "@sentry/react";
import {
  PaymentElement,
  PaymentElementProps,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { ConfirmPaymentData } from "@stripe/stripe-js";

import * as Events from "@app/analytics/tracking/events";
import type { CheckoutBillingInfo, ClientSecretType } from "@app/common/types";

import Button from "@rui/Button";
import Stack from "@rui/Stack";

import {
  INVALID_ELEMENT_STATE_ERROR,
  DEFAULT_PAYMENT_CONFIRM_ERROR,
  PURCHASE_SUBMIT_BUTTON_ID,
} from "./constants";

interface PaymentFormProps {
  billingInfo: CheckoutBillingInfo;
  clientSecret?: string;
  isHidden: boolean;
  onSubmitSettled: (error?: string) => void;
  type?: ClientSecretType;
  paymentConfirmReturnUrl: string;
  onLoadError: PaymentElementProps["onLoadError"];
}

const PaymentForm = ({
  billingInfo,
  clientSecret,
  isHidden,
  onSubmitSettled,
  paymentConfirmReturnUrl,
  type,
  onLoadError,
}: PaymentFormProps) => {
  const elements = useElements();
  const stripe = useStripe();

  const handleConfirmPurchase = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (!stripe || !type) {
      Sentry.captureMessage(
        `Stripe Error: stripe=${Boolean(stripe)}, type=${type}`
      );
      return onSubmitSettled(INVALID_ELEMENT_STATE_ERROR);
    }

    const address = billingInfo.address;
    const name = billingInfo.name;

    const confirmParams: ConfirmPaymentData = {
      return_url: paymentConfirmReturnUrl,
      payment_method_data: {
        billing_details: {
          name,
          address: {
            line1: address?.line1,
            line2: address?.line2 ?? "",
            city: address?.city,
            state: address?.state,
            postal_code: address?.postalCode,
            country: address?.country,
          },
        },
      },
    };

    let errorMessage;
    const confirmFn =
      type === "setup_intent" ? stripe.confirmSetup : stripe.confirmPayment;

    if (isHidden) {
      // if the payment element is hidden, we need to use the client secret to confirm setup/payment,
      // otherwise we'll see an error that card info is missing.
      Sentry.addBreadcrumb({
        message: `payment element hidden: ${type}`,
        level: "log",
      });

      if (!clientSecret) {
        Sentry.captureMessage(`Stripe Error: No client secret`);
        return onSubmitSettled(INVALID_ELEMENT_STATE_ERROR);
      }

      const { error } = await confirmFn({
        clientSecret,
        confirmParams,
      });

      if (error) errorMessage = error.message ?? DEFAULT_PAYMENT_CONFIRM_ERROR;
    } else {
      // if the payment element is visible, we want to use the new card info for payment, so
      // confirm using the elements
      Sentry.addBreadcrumb({
        message: `payment element visible: ${type}`,
        level: "log",
      });

      if (!elements) {
        Sentry.captureMessage(`Stripe Error: No elements`);
        return onSubmitSettled(INVALID_ELEMENT_STATE_ERROR);
      }

      const { error } = await confirmFn({
        elements,
        confirmParams,
      });

      if (error) errorMessage = error.message ?? DEFAULT_PAYMENT_CONFIRM_ERROR;
    }

    // For analytics purposes
    Events.userPurchaseError({ message: errorMessage ?? "generic error" });

    // some errors will be caught immediately by Stripe (and will not go through the return_url).
    // in that case, show the error to the user, since putting in a new card or updating the
    // billing details should fix most of these immediate errors. asynchronous errors will be handled
    // in the BE via the return_url
    onSubmitSettled(errorMessage);
  };

  return (
    <>
      <PaymentElement
        options={{ fields: { billingDetails: { address: "never" } } }}
        onLoadError={onLoadError}
      />
      {/* Hidden button that will get programmatically clicked when the user clicks the subscribe button */}
      <Stack sx={{ height: "0px", width: "0px", overflow: "hidden" }}>
        <Button id={PURCHASE_SUBMIT_BUTTON_ID} onClick={handleConfirmPurchase}>
          Subscribe
        </Button>
      </Stack>
    </>
  );
};

export default PaymentForm;
